import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { FaSave } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { FormBuilder, FormStateBuilder } from '../../../../components/FormBuilder';
import ContentContainer from '../../../../components/ContentContainer';
import Loading from '../../../../components/Loading';
import useAlertNotification from '../../../../context/AlertNotificationContext';
import ErrorRequestService from '../../../../services/errorRequest';
import RegimeApiService from '../../../../services/api/RegimeApiService';
import RegimeValidation from '../../../../services/validation/RegimeValidation';
import { storeRegimes } from '../../../../store/actions/dadosActions';
import UtilService from '../../../../services/util';
import RegimeImpostoFormViewInfo from './RegimeImpostoFormViewInfo';
import RegimeImpostoFormNewInfo from './RegimeImpostoFormNewInfo';

let regime = {cod:""};

/**
 * Formulário que associa um imposto a um regime de contratação,
 * criando o relacionamento entre esse imposto e esse regime para uso nos cálculos
 */
function RegimeImpostoForm({dados, onSave, onCancel, editable=false}) {
    // HOOKS
    const match = useRouteMatch();
    const location = useLocation();
    const history = useHistory();
    const alert = useAlertNotification();

    // REDUX
    const { regimes } = useSelector(state => state.dados);
    const dispatch = useDispatch();
    const reduxStoreBenefs = useCallback(
        (reg) => dispatch( storeRegimes(reg) ),
        [dispatch]
    )

    // PROPS
    const { cod: regime_cod, id } = dados || {};
    const { cod: imposto_cod, sigla='' } = location.state || {};
    const isFormView = !!regime_cod;

    // STATE
    const [formFields, setFormFields] = useState({});
    const [origData, setOrginData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [edit, setEdit] = useState([]);
    const [isEdited, setIsEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // FUNCTIONS
    const isFormEdited = () => {
        if (!edit.length) return false;
        const jsonOrigData = JSON.stringify(origData);
        const jsonFormData = JSON.stringify(formData);
        return (jsonOrigData !== jsonFormData);
    }
        
    const askForLeave = (cbPositive, cbNegative=()=>{}) => {
        if (cbPositive instanceof Function) {
            const msg = 'Foram feitas alterações!\nDeseja realmente sair e perdê-las?';
            const leave = window.confirm(msg);
            
            if (leave) cbPositive();
            else cbNegative();
        }   
        return false;
    }

    const resetForm = () => UtilService.genericResetFormFunction();
    
    const handleOnCancel = () => {
        if (onCancel) {
            onCancel();
            return;
        }
        history.goBack();
    }

    const hanldeOnSave = () => {
        if (onSave) {
            onSave();
            return;
        } 
        const url = `${match.url.replace(`/${imposto_cod}`, '')}`;
        history.replace(url, location.state);
    }

    const saveForm = async () => {
        try {
            // validar form
            const validation = await RegimeValidation.validate(formData);
            if (!validation.isValid) {
                alert.error('Erros no formulário!');
                return;
            }
            
            // se ok, enviar p/ API
            const resp = !isFormView // se novo, ou existente
               /* NEW  */ ? await RegimeApiService.addRegimeImposto(imposto_cod, formData.regime_cod).then(r => r.data.result.insertId)
               /* VIEW */ : await RegimeApiService.updateRegimeImposto(imposto_cod, {...formData, id}).then(r => r.data.result.affectedRows);
            
            // se contagem vier 0, não inseriu nem atualizou
            if (resp <= 0) throw new Error('Dados de regime-imposto não inseridos/atualizados.');

            setIsEdited(false);
            // setEdit(false);
            alert.success("Dados do regime para o imposto atualizados!");
            hanldeOnSave();
        }
        catch (err) {
            const errorService = new ErrorRequestService(err);
            const errorMsg = errorService.getErrors(); 
            console.log(errorMsg);
            alert.error(errorMsg);
        }
    }

    const handleCancel = () => {
        if (isEdited) {
            askForLeave(() => {
                setIsEdited(false);
                setFormData(origData);
                resetForm();
                handleOnCancel();
            });
            return;
        }
        handleOnCancel();
    }
    
    const onChange = (ev) => {
        const { value, name } = ev.target;
        const newform = {...formData, [name]: value};
        setFormData(newform);
        setEdit([...edit, name]);
    }

    // Pega o regime escolhido
    const getBenefChosen = () => {
        const { regime_cod } = formData;
        const reg = regimes.find(b => {
            return b.cod == regime_cod
        });
        regime = {...reg, regime_cod};
        setFormData({...regime});
        setOrginData({...regime});
    }

    const onEdit = () => {        
        // verifica os dados do regime escolhido
        const { regime_cod } = formData || {};
        if (!isFormView && !!regime_cod && regime_cod != regime.cod) {
            getBenefChosen();
        }
        // verifica se formulário foi editado
        if (isFormEdited() && !isEdited) setIsEdited(true);
    };

    const onStart = () => {
        const getData = async () => {
            try {
                const regimeData = regimes || await RegimeApiService.getAll().then(resp => {
                    const { data } = resp.data;
                    return (data.sort((a, b) => ((a.sigla < b.sigla) ? -1 : 1))) || [];
                });
                if (!regimes) reduxStoreBenefs(regimeData);
                
                const formCampos = (isFormView)
                    ? RegimeImpostoFormViewInfo.getFormFields()
                    : RegimeImpostoFormNewInfo.getFormFields(regimeData);

                const formDados = (isFormView)
                    ? [...regimeData].find(item => item.cod == regime_cod)
                    : FormStateBuilder(formCampos);

                setFormFields( formCampos );
                setFormData( formDados );
                setOrginData( formDados );
            }
            catch (err) {
                const errorService = new ErrorRequestService(err);
                const errorMsg = errorService.getErrors(); 
                console.log(errorMsg);
                alert.error(errorMsg);
            }
            finally {
                setIsLoading(false);
            }
        }
        getData();
    }

    // USEEFFECTS
    useEffect(onStart, []);
    useEffect(onEdit, [formData]);

    const actions = [
        { 
            label: 'Cancelar',
            icon: <MdClose /> ,
            color: 'danger',
            type: 'button',
            onClick: handleCancel
        }
    ]

    if (editable) {
        actions.unshift(
            { 
                label: 'Salvar',
                icon: <FaSave />,
                color: 'success',
                type: 'button',
                onClick: saveForm
            }
        );
    }
    
    // const { nome: reg } = formData || {};
    const title = `Regime ao Imposto ${sigla}`;

    return (
        <ContentContainer
            title={title}
            actions={actions}
        >
            { isLoading 
                ? <Loading message="Carregando..." />
                : ( origData && 
                    <FormBuilder 
                        formFields={formFields}
                        formData={formData}
                        onChange={onChange}
                        disabledForm={!editable}
                        initialValues={origData}
                        schema={RegimeValidation.schema}
                    />
                )
            }
            
            <Prompt
                when={isEdited}
                message={location => askForLeave(() => {
                    setIsEdited(false);
                    setTimeout(() => {
                        resetForm();
                        history.push(location.pathname);
                    }, 100);
                })}
            />
        </ContentContainer>
    )
}

export default RegimeImpostoForm;
